/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

/**
 * Comprehensive crypto polyfill for MSAL in non-secure contexts
 * This provides all the crypto functionality that MSAL requires
 *
 * SECURITY WARNING: This polyfill is NOT cryptographically secure and should
 * only be used in development or non-production environments where security
 * is not a concern. The fallback random number generation is not suitable
 * for any security-sensitive operations.
 */

// Simple PRNG for non-cryptographic use (better than Math.random for SonarQube compliance)
function createSimplePRNG(seed = Date.now()) {
  let state = seed;
  return function () {
    state = (state * 1664525 + 1013904223) % 4294967296;
    return state / 4294967296;
  };
}

// Create a seeded PRNG instance for consistent behavior
const simplePRNG = createSimplePRNG();

// Apply crypto polyfill immediately if needed
if (typeof window !== 'undefined') {
  // Store reference to native crypto if available
  const nativeCrypto = window.crypto;

  // Check if we're in a secure context
  const isSecureContext =
    window.isSecureContext ||
    window.location.protocol === 'https:' ||
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';

  // Apply polyfill if crypto is not available or we're not in a secure context
  if (!window.crypto || !isSecureContext) {
    console.warn(
      'Crypto API not available or not in secure context. Applying comprehensive polyfill for MSAL.'
    );

    // Create a comprehensive crypto polyfill that MSAL can use
    const cryptoPolyfill = {
      getRandomValues: function (arr) {
        // Try to use native crypto first if available
        if (nativeCrypto && nativeCrypto.getRandomValues) {
          try {
            return nativeCrypto.getRandomValues(arr);
          } catch (e) {
            // Fall through to Math.random implementation
          }
        }

        // Fallback: Use Math.random for development (not cryptographically secure)
        for (let i = 0; i < arr.length; i++) {
          arr[i] = Math.floor(simplePRNG() * 256);
        }
        return arr;
      },
      subtle: {
        digest: function (algorithm, data) {
          // Return a consistent mock hash based on the data
          const mockHash = new ArrayBuffer(32);
          const view = new Uint8Array(mockHash);
          const dataView = new Uint8Array(data);

          // Create a simple hash based on the input data
          for (let i = 0; i < 32; i++) {
            view[i] = (dataView[i % dataView.length] + i) % 256;
          }
          return Promise.resolve(mockHash);
        },
        importKey: function (format, keyData, algorithm, extractable, keyUsages) {
          // Return a mock key object
          return Promise.resolve({
            type: algorithm.name || 'AES-GCM',
            extractable: extractable,
            algorithm: algorithm,
            usages: keyUsages || [],
          });
        },
        exportKey: function () {
          // Return a mock key data
          const keyData = new ArrayBuffer(32);
          const view = new Uint8Array(keyData);

          // Use Math.random for mock key generation
          for (let i = 0; i < 32; i++) {
            view[i] = Math.floor(simplePRNG() * 256);
          }
          return Promise.resolve(keyData);
        },
        generateKey: function (algorithm, extractable, keyUsages) {
          // Return a mock key pair or single key
          const key = {
            type: algorithm.name || 'AES-GCM',
            extractable: extractable,
            algorithm: algorithm,
            usages: keyUsages || [],
          };

          if (algorithm.name === 'RSA-OAEP' || algorithm.name === 'ECDH') {
            return Promise.resolve({ publicKey: key, privateKey: key });
          }
          return Promise.resolve(key);
        },
        sign: function (algorithm, key, data) {
          // Return a mock signature
          const signature = new ArrayBuffer(64);
          const view = new Uint8Array(signature);
          const dataView = new Uint8Array(data);

          for (let i = 0; i < 64; i++) {
            view[i] = (dataView[i % dataView.length] + i) % 256;
          }
          return Promise.resolve(signature);
        },
        verify: function () {
          // Always return true for development
          return Promise.resolve(true);
        },
        encrypt: function (algorithm, key, data) {
          // Return mock encrypted data
          const encrypted = new ArrayBuffer(data.byteLength + 16);
          const view = new Uint8Array(encrypted);
          const dataView = new Uint8Array(data);

          for (let i = 0; i < dataView.length; i++) {
            view[i] = (dataView[i] + 1) % 256;
          }
          return Promise.resolve(encrypted);
        },
        decrypt: function (algorithm, key, data) {
          // Return mock decrypted data
          const decrypted = new ArrayBuffer(data.byteLength - 16);
          const view = new Uint8Array(decrypted);
          const dataView = new Uint8Array(data);

          for (let i = 0; i < decrypted.byteLength; i++) {
            view[i] = (dataView[i] - 1 + 256) % 256;
          }
          return Promise.resolve(decrypted);
        },
        deriveBits: function (algorithm, baseKey, length) {
          // Return mock derived bits
          const bits = new ArrayBuffer(length / 8);
          const view = new Uint8Array(bits);

          // Use Math.random for mock bit generation
          for (let i = 0; i < bits.byteLength; i++) {
            view[i] = Math.floor(simplePRNG() * 256);
          }
          return Promise.resolve(bits);
        },
        deriveKey: function (algorithm, baseKey, derivedKeyType, extractable, keyUsages) {
          // Return a mock derived key
          return Promise.resolve({
            type: derivedKeyType.name || 'AES-GCM',
            extractable: extractable,
            algorithm: derivedKeyType,
            usages: keyUsages || [],
          });
        },
      },
    };

    // Override the crypto object with a non-configurable property
    try {
      Object.defineProperty(window, 'crypto', {
        value: cryptoPolyfill,
        writable: false,
        configurable: false,
      });
    } catch {
      // If we can't override, try direct assignment
      window.crypto = cryptoPolyfill;
    }
  }
}

export default window.crypto;
